import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
  Bell,
  BookMarked,
  Brain,
  Building2,
  CreditCard,
  HelpCircle,
  Keyboard,
  Lock,
  LogOut,
  Palette,
  Plug,
  ShieldCheck,
  ShieldQuestion,
  RefreshCw,
  Sparkles,
  Terminal,
  UserCircle,
  type LucideIcon,
} from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  LogoutOverlay,
  Separator,
  cn,
} from "@ando/ui";
import { getSession, isPlatformAdmin } from "@ando/core/auth";

import { logoutAndRedirect } from "@/features/auth/session";
import { initialsOf } from "@/lib/initials";
// Deep imports (NOT the console barrel) — the barrel re-exports the console
// pages and would pull them into the product shell chunk.
import { surfaceAccess } from "@/features/console/access";
import { setPreferredSurface } from "@/features/console/lib/surface";
import { useUserMenuStore } from "../state/userMenuStore";
import { OrgSwitcherDialog } from "./OrgSwitcherDialog";
import { useKeyboardShortcutsOverlay } from "@/features/help/state/keyboardShortcutsStore";

/**
 * UserMenuButton — avatar (mounted bottom of LEFT mini-rail) opening a
 * LEFT-side drawer with identity + direct-jumps to every platform area.
 *
 * Jun 13, 2026 rebuild — "tutto come /integrations"
 * -------------------------------------------------
 * Earlier revisions oscillated between:
 *   1. mirroring the whole /settings IA inside the drawer (doubled menu)
 *   2. shrinking the drawer to a single "Impostazioni" CTA (nested
 *      navigation, "doppio livello di menù")
 *
 * Ando has ~9 settings-or-area destinations total (Profilo / Sicurezza /
 * Privacy & dati / Fatturazione / Modi agente / Notifiche / Brand Kit /
 * Connettori / Admin · panel). That's small enough to expose them all
 * here as direct jumps — Stripe Dashboard / Slack 2026 / Discord pattern.
 * The /settings shell dropped its left rail; each ``/settings/*`` child
 * now renders full-screen, peer to ``/integrations`` and ``/admin``.
 *
 * Sections
 * --------
 *   Header       — Avatar · Name · Email · Org switcher chip
 *   ACCOUNT      — Profilo · Sicurezza · Privacy & dati
 *   WORKSPACE    — Connettori · Fatturazione
 *   ESPERIENZA   — Modi agente · Notifiche · Brand Kit
 *   UTILITÀ      — Tasti rapidi (⌘/) · Help center
 *   ───────────
 *   Admin · panel (admin only)
 *   Logout (danger)
 *
 * Spatial continuity: ``side="left"`` so the drawer flows out from under
 * the left-rail avatar (Linear/Vercel/Notion all do the same). Mutex with
 * Studio drawer via `userMenuStore.open()`. ESC closes via Radix Dialog.
 */

// ── MenuItem ────────────────────────────────────────────────────────────

interface MenuItemProps {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly danger?: boolean;
  readonly onClick?: () => void;
  /** Optional right-side annotation: count, keyboard hint, or "Nuovo" badge. */
  readonly hint?: React.ReactNode;
}

/**
 * Single drawer row. ``role="menuitem"`` is set on the wrapping
 * ``role="menu"`` group so arrow-key nav can target these via the
 * roving-tabindex helper below.
 */
const MenuItem = React.forwardRef<HTMLButtonElement, MenuItemProps>(
  ({ icon: Icon, label, danger, onClick, hint }, ref) => (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        "inline-flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-left font-medium text-[13px] transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/60 focus-visible:ring-offset-1",
        danger
          ? "text-error hover:bg-error/10"
          : "text-ink-2 hover:bg-black/[0.05] hover:text-ink-1",
      )}
    >
      <Icon
        size={15}
        strokeWidth={1.75}
        className={danger ? "text-error" : "text-ink-3"}
        aria-hidden
      />
      <span className="flex-1 truncate">{label}</span>
      {hint != null ? (
        <span className="ml-auto inline-flex items-center text-[11px] text-ink-3 tabular-nums">
          {hint}
        </span>
      ) : null}
    </button>
  ),
);
MenuItem.displayName = "MenuItem";

// ── Section header ──────────────────────────────────────────────────────

const SectionLabel: React.FC<{ readonly children: React.ReactNode }> = ({ children }) => (
  <div className="mt-2 px-2.5 pb-1 font-semibold text-[10px] text-ink-3/80 uppercase tracking-[0.08em]">
    {children}
  </div>
);

// ── Keyboard-hint kbd chip (used by AIUTO · Tasti rapidi) ───────────────

const Kbd: React.FC<{ readonly children: React.ReactNode }> = ({ children }) => (
  <kbd
    aria-hidden
    className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[4px] border border-hairline bg-white px-1 font-mono text-[10px] text-ink-2 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
  >
    {children}
  </kbd>
);

/**
 * Roving tabindex — arrow ↑/↓ nav across the menu items inside the
 * drawer. Plain DOM query against ``[role="menuitem"]`` within the
 * provided container ref; no external dep. Home/End jump to first/last.
 */
function useRovingMenuKeys(
  containerRef: React.RefObject<HTMLDivElement | null>,
  active: boolean,
): void {
  React.useEffect(() => {
    if (!active) return;
    const root = containerRef.current;
    if (!root) return;

    function items(): HTMLElement[] {
      const r = containerRef.current;
      if (!r) return [];
      return Array.from(r.querySelectorAll<HTMLElement>('[role="menuitem"]'));
    }

    function onKey(e: KeyboardEvent): void {
      const all = items();
      if (all.length === 0) return;
      const idx = all.indexOf(document.activeElement as HTMLElement);
      let next = idx;
      switch (e.key) {
        case "ArrowDown":
          next = idx < 0 ? 0 : (idx + 1) % all.length;
          break;
        case "ArrowUp":
          next = idx < 0 ? all.length - 1 : (idx - 1 + all.length) % all.length;
          break;
        case "Home":
          next = 0;
          break;
        case "End":
          next = all.length - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      all[next]?.focus();
    }

    root.addEventListener("keydown", onKey);
    return () => root.removeEventListener("keydown", onKey);
  }, [containerRef, active]);
}

// ── Main component ──────────────────────────────────────────────────────

export const UserMenuButton: React.FC<{ readonly className?: string }> = ({
  className,
}) => {
  const { i18n } = useLingui();
  const isOpen = useUserMenuStore((s) => s.isOpen);
  const open = useUserMenuStore((s) => s.open);
  const close = useUserMenuStore((s) => s.close);
  const navigate = useNavigate();

  const session = getSession();
  const userName = session?.display_name || session?.email || i18n._(msg`Utente`);
  const userEmail = session?.email ?? "";
  const orgLabel = session?.org_id ? i18n._(msg`Organizzazione`) : "—";
  const initials = initialsOf(userName, userEmail);

  // Platform-admin gate — the SAME ``isPlatformAdmin`` helper the ``/admin``
  // route guards on, mirroring the BE ``require_platform_admin`` (boolean
  // ``is_platform_admin`` OR role∈{admin,superadmin}). ``role`` is the source of
  // truth; the boolean is a legacy duplicate. NOT ``useHasCapability("admin")``:
  // "admin" is not a manifest capability and that hook fails OPEN on unknown
  // slots, which would show admin rows to every user.
  const isAdmin = isPlatformAdmin(session);

  // Dual-surface entitlement — the "Developer Console" jump renders ONLY
  // when this session may enter the Platform Console (role-based, see
  // ``surfaceAccess``). Navigating there records "console" as the last-used
  // surface so the post-login landing restores it.
  const canEnterConsole = surfaceAccess(session).console;

  // Keyboard shortcuts overlay opener — shared global Cmd+/ surface.
  const openShortcuts = useKeyboardShortcutsOverlay((s) => s.open);

  // Org switcher dialog state — mounted outside the avatar Drawer so it
  // can take focus cleanly without competing with the drawer's own
  // focus-trap.
  const [orgSwitcherOpen, setOrgSwitcherOpen] = React.useState(false);
  const openOrgSwitcher = React.useCallback((): void => {
    close();
    setOrgSwitcherOpen(true);
  }, [close]);

  // Wrap navigate so every menu click closes the drawer + routes in one
  // step. ``replace: true`` avoids polluting browser history with the
  // avatar-drawer click (the chat tab stays the back-button target).
  const go = React.useCallback(
    (to: string) => (): void => {
      close();
      navigate({ to, replace: true });
    },
    [close, navigate],
  );

  const handleOpenConsole = React.useCallback((): void => {
    close();
    setPreferredSurface("console");
    navigate({ to: "/console", replace: true });
  }, [close, navigate]);

  // Logout takeover (Aug 27, 2026): paint the branded overlay the instant
  // the user commits — the teardown + hard redirect can take seconds and the
  // app must read as "Ando working", never as frozen.
  const [loggingOut, setLoggingOut] = React.useState(false);
  const handleLogout = React.useCallback((): void => {
    close();
    setLoggingOut(true);
    void logoutAndRedirect();
  }, [close]);

  const handleOpenShortcuts = React.useCallback((): void => {
    close();
    openShortcuts();
  }, [close, openShortcuts]);

  // Container ref + roving keys — only attaches when the drawer is open
  // so we don't capture arrow keys for the rest of the app.
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  useRovingMenuKeys(menuRef, isOpen);

  return (
    <>
      <LogoutOverlay
        open={loggingOut}
        label={i18n._(msg`Disconnessione in corso…`)}
        subline={i18n._(msg`A presto`)}
      />
      <Drawer open={isOpen} onOpenChange={(o) => (o ? open() : close())}>
        <DrawerTrigger asChild>
          <button
            type="button"
            aria-label={i18n._(msg`Menu utente`)}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            className={cn(
              // Pencil avatar — orange ring, empty fill, orange initials. Safe
              // for the single, always-same user avatar (no per-person colour
              // coding to lose, unlike contact avatars).
              "inline-flex h-9 w-9 items-center justify-center rounded-full",
              "border-[1.5px] border-ando-orange bg-transparent",
              "font-semibold text-[12px] text-ando-orange",
              // Warm halo — same orange glow as the logo + pencil buttons.
              "shadow-[0_2px_12px_-2px_rgba(255,122,26,0.28)]",
              "transition-all hover:bg-ando-orange/[0.08] hover:shadow-[0_4px_16px_-2px_rgba(255,122,26,0.4)] active:scale-[0.95]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/60 focus-visible:ring-offset-2",
              className,
            )}
          >
            {initials}
          </button>
        </DrawerTrigger>
        <DrawerContent
          side="left"
          className="w-[320px] gap-0 p-0"
          ariaTitle={i18n._(msg`Menu utente`)}
          hideCloseButton
        >
          {/* ── Profile header — fixed at the top, doesn't scroll ──────── */}
          <header className="px-3 pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div
                aria-hidden
                className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-[1.5px] border-ando-orange bg-transparent font-semibold text-[16px] text-ando-orange shadow-[0_2px_12px_-2px_rgba(255,122,26,0.28)]"
              >
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className="truncate font-semibold text-[14px] text-ink-1"
                  title={userName}
                >
                  {userName}
                </div>
                <div
                  className="truncate text-[12px] text-ink-3"
                  title={userEmail}
                >
                  {userEmail}
                </div>
              </div>
            </div>

            {/* Org chip — small inline affordance to swap organisation.
                Sits directly under the email so the org context is
                always one click away (Jun 13, 2026 refactor dropped
                the big "Impostazioni" CTA — settings sections are now
                direct-jump rows in the menu body below, no nested
                navigation needed). */}
            <button
              type="button"
              onClick={openOrgSwitcher}
              className={cn(
                "mt-2.5 inline-flex h-7 w-full items-center gap-2 rounded-md px-2.5 text-left text-[11px] text-ink-3 transition-colors",
                "hover:bg-black/[0.04] hover:text-ink-2",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/60",
              )}
            >
              <Building2 size={11} strokeWidth={1.75} aria-hidden />
              <span className="flex-1 truncate">{orgLabel}</span>
              <RefreshCw size={10} strokeWidth={1.75} aria-hidden className="opacity-60" />
            </button>
          </header>

          <Separator className="my-0" />

          {/* ── Scrollable middle — sections + admin block ─────────────── */}
          <div
            ref={menuRef}
            role="menu"
            aria-label={i18n._(msg`Impostazioni`)}
            className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-1.5 py-1.5"
          >
            {/* Jun 13, 2026 — "tutto come /integrations". The /settings
                shell dropped its left rail (PlatformAreaLayout); each
                sub-section is now reachable directly from this menu.
                Grouped Account / Workspace / Esperienza to preserve the
                IA scan, but every row is a top-level jump — no nested
                navigation, no doubled menu. */}
            <SectionLabel>{i18n._(msg`Account`)}</SectionLabel>
            <MenuItem
              icon={UserCircle}
              label={i18n._(msg`Profilo`)}
              onClick={go("/settings")}
            />
            <MenuItem
              icon={ShieldCheck}
              label={i18n._(msg`Sicurezza`)}
              onClick={go("/settings/security")}
            />
            <MenuItem
              icon={Lock}
              label={i18n._(msg`Privacy & dati`)}
              onClick={go("/settings/privacy")}
            />

            <SectionLabel>{i18n._(msg`Workspace`)}</SectionLabel>
            <MenuItem
              icon={Plug}
              label={i18n._(msg`Connettori`)}
              onClick={go("/integrations")}
            />
            <MenuItem
              icon={Building2}
              label={i18n._(msg`Organizzazione`)}
              onClick={go("/settings/organization")}
            />
            <MenuItem
              icon={CreditCard}
              label={i18n._(msg`Fatturazione`)}
              onClick={go("/settings/billing")}
            />
            {/* Dual-surface switch — console-entitled sessions only. */}
            {canEnterConsole ? (
              <MenuItem
                icon={Terminal}
                label={i18n._(msg`Developer Console`)}
                onClick={handleOpenConsole}
              />
            ) : null}

            <SectionLabel>{i18n._(msg`Esperienza`)}</SectionLabel>
            <MenuItem
              icon={Sparkles}
              label={i18n._(msg`Modi agente`)}
              onClick={go("/settings/modes")}
            />
            <MenuItem
              icon={BookMarked}
              label={i18n._(msg`Skill`)}
              onClick={go("/settings/skills")}
            />
            <MenuItem
              icon={Bell}
              label={i18n._(msg`Notifiche`)}
              onClick={go("/settings/notifications")}
            />
            <MenuItem
              icon={Brain}
              label={i18n._(msg`Memoria personale`)}
              onClick={go("/settings/memory")}
            />
            <MenuItem
              icon={Palette}
              label={i18n._(msg`Brand Kit`)}
              onClick={go("/settings/brand-kits")}
            />

            {/* Wave 2 Clean & Control — DAMA quality cockpit lives under
                /workspace/clean-control (the surface renders its own
                SurfaceLayout chrome and the review queue) but the entry
                point is here in the drawer's "Esperienza" section so it
                stays next to Memoria / Notifiche and doesn't require a
                new WorkspaceTab enum value. */}
            <MenuItem
              icon={ShieldQuestion}
              label={i18n._(msg`Clean & Control`)}
              onClick={go("/workspace/clean-control")}
            />

            <SectionLabel>{i18n._(msg`Utilità`)}</SectionLabel>
            <MenuItem
              icon={Keyboard}
              label={i18n._(msg`Tasti rapidi`)}
              onClick={handleOpenShortcuts}
              hint={
                <span className="inline-flex items-center gap-0.5">
                  <Kbd>⌘</Kbd>
                  <Kbd>/</Kbd>
                </span>
              }
            />
            {/* "Novità" (changelog) hidden Jul 2026 — unused. The
                /settings/changelog route stays reachable by URL. */}
            <MenuItem icon={HelpCircle} label={i18n._(msg`Help center`)} onClick={go("/settings/help")} />

            {/* Admin · panel — gated on the ``admin`` capability. Placed
                at the bottom near the destructive logout so admin tools
                are deliberately one extra glance away (avoids accidental
                "admin · panel" clicks on the way to logout). */}
            {isAdmin ? (
              <>
                <Separator className="my-1.5" />
                <SectionLabel>{i18n._(msg`Admin`)}</SectionLabel>
                <MenuItem icon={ShieldCheck} label={i18n._(msg`Admin · panel`)} onClick={go("/admin")} />
              </>
            ) : null}
          </div>

          {/* ── Logout pinned at the bottom ────────────────────────────── */}
          <Separator className="my-0" />
          <div className="p-1.5">
            <MenuItem icon={LogOut} label={i18n._(msg`Esci`)} danger onClick={handleLogout} />
          </div>
        </DrawerContent>
      </Drawer>
      <OrgSwitcherDialog open={orgSwitcherOpen} onOpenChange={setOrgSwitcherOpen} />
    </>
  );
};

UserMenuButton.displayName = "UserMenuButton";
